import React from 'react'
import { Checkbox, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { TrashIcon } from '../../../icons/FormIcons'
import { EyeIcon, LinkIcon, PencilIcon } from '../../../icons/SettingsIcons'
import { Device, Storage } from 'src/Libs/flow/installation/forms'
import { getDeviceDefaultName } from '../../../parsers/device-parsers/get-device-default-name'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DeviceStatusMap, useInstallationDeviceStatuses, useInstallationForm, useStorageBatteryOverview } from 'src/Libs/flow/installation/hooks'
import { BatteryTrainingMode } from 'src/Libs/system-data-handler'
import { getDeviceNavigationLink } from '../../utils/get-device-navigation-link'
import { scrollElementIntoView, useScrollContainer } from 'src/Libs/flow/utils'
import { DeviceIcon } from 'src/Libs/flow/icons/DeviceIcon'
import { getDeviceParsedStatus } from 'src/Libs/flow/parsers/device-parsers/get-device-parsed-status'
import { DeviceStatusIcon } from './DeviceStatusIcon'
import { getHasNonEmcDevices } from '../../utils'
import { useDevicePairing } from '../../hooks'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

type DevicesTableProps = {
  devices: Device[]
  selectedDeviceIds: { [deviceId: string]: true }
  toggleDeviceSelection: (id: string) => void
  handleDelete: () => void
}

export const DevicesTableDesktop: React.FC<DevicesTableProps> = ({ devices, selectedDeviceIds, toggleDeviceSelection, handleDelete }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const hasNonEmcDevices = getHasNonEmcDevices(devices)

  const { control } = useInstallationForm()
  const installationId = useWatch({ control, name: 'id' })

  const storage = devices.find(device => device.deviceCategory === 'Storage' && device.isPaired) as Storage
  const isActiveStorage = !!storage?.deviceId && !!storage?.pairingId
  const { data } = useStorageBatteryOverview(installationId || undefined, storage?.storageHardwareDeviceId ?? null, isActiveStorage)
  const trainingMode: BatteryTrainingMode = data?.batTrainingMode ?? 0

  const latestDeviceStatuses = useInstallationDeviceStatuses(installationId)

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('installation.summaryTable.deviceName')}</TableCell>
            <TableCell>{t('installation.summaryTable.deviceId')}</TableCell>
            <TableCell align="center">{t('installation.summaryTable.pairingStatus')}</TableCell>
            <TableCell>{t('installation.summaryTable.pairing')}</TableCell>
            {canEdit && (
              <TableCell width={42} align="center">
                <IconButton size="small" disabled={!Object.values(selectedDeviceIds).length} onClick={handleDelete}>
                  <TrashIcon />
                </IconButton>
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {!devices.length ? (
            <TableRow sx={{ '&:last-child td': { border: 0 } }}>
              <TableCell colSpan={5} align="center">
                <Typography pt={2} color="textDisabled">
                  {t('shell.noData')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            devices.map(device => (
              <DeviceTableRow
                key={device.id}
                device={device}
                latestDeviceStatuses={latestDeviceStatuses}
                hasNonEmcDevices={hasNonEmcDevices}
                trainingMode={trainingMode}
                selectedDeviceIds={selectedDeviceIds}
                toggleDeviceSelection={toggleDeviceSelection}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

type DeviceTableRowProps = {
  device: Device
  latestDeviceStatuses: DeviceStatusMap
  hasNonEmcDevices: boolean
  trainingMode: BatteryTrainingMode
  selectedDeviceIds: { [deviceId: string]: true }
  toggleDeviceSelection: (id: string) => void
}

const DeviceTableRow: React.FC<DeviceTableRowProps> = ({
  device,
  latestDeviceStatuses,
  hasNonEmcDevices,
  trainingMode,
  selectedDeviceIds,
  toggleDeviceSelection,
}) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const {
    featureFlags: { showDetailedDeviceErrorMessages },
  } = useFeatureFlagsContext()

  const { canPair, handlePairing } = useDevicePairing(device)
  const scrollContainer = useScrollContainer()

  const deviceWithLatestStatus = { ...device, ...latestDeviceStatuses.get(device.deviceId) }

  return (
    <TableRow sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <DeviceIcon device={device.deviceCategory} sx={{ fontSize: 30 }} />
          {getDeviceDefaultName(device.name, device.deviceCategory, device.deviceId, t)}
          <IconButton
            size="small"
            component={Link}
            to={`../${getDeviceNavigationLink(device)}`}
            onClick={() => scrollElementIntoView(scrollContainer, device.navigationId)}
          >
            <EyeIcon />
          </IconButton>
        </Stack>
      </TableCell>
      <TableCell>{device.pairingId}</TableCell>
      <TableCell align="center">
        <DeviceStatusIcon {...getDeviceParsedStatus(deviceWithLatestStatus, showDetailedDeviceErrorMessages, trainingMode)} />
      </TableCell>
      <TableCell>
        <IconButton size="small" disabled={!canEdit || !canPair} onClick={handlePairing}>
          {device.pairingId ? <PencilIcon /> : <LinkIcon />}
        </IconButton>
      </TableCell>
      {canEdit && (
        <TableCell padding="none" align="center">
          <Checkbox
            disabled={device.deviceCategory === 'EMC' && hasNonEmcDevices}
            checked={!!selectedDeviceIds[device.id]}
            onChange={() => toggleDeviceSelection(device.id)}
          />
        </TableCell>
      )}
    </TableRow>
  )
}
